// 選択ハンドルのうち、回転ハンドル用のドラッグハンドラ。
//
// - pointerdown でドラッグ開始（基準角度を捕捉）
// - pointermove でオブジェクト中心とポインタ位置から角度差分を計算し、対象 DOM の
//   `transform: rotate(deg)` を即時プレビュー（doc store/サーバへは送らない）
// - pointerup で最終 rotation を transformObject op として送信し永続化
//
// Shift 押下時は 15° 単位にスナップする。

import { useEffect, type RefObject } from "react";
import { useCanvasStore } from "../store/canvas-store";
import type { CanvasOpDispatcher } from "../use-canvas-op-dispatcher";

interface Options {
  readonly handleRef: RefObject<HTMLElement | null>;
  readonly objectId: string;
  readonly dispatcher: CanvasOpDispatcher;
  /** オブジェクトのステージ座標における中心。回転計算の原点になる。 */
  readonly centerX: number;
  readonly centerY: number;
  readonly initialRotation: number;
  /** プレビュー対象の DOM 要素。`transform: rotate(...)` を直接書き換える。 */
  readonly targetRef: RefObject<HTMLElement | null>;
}

const SNAP_ANGLE_DEG = 15;

export function useRotationHandle(options: Options): void {
  const { handleRef, objectId, dispatcher, centerX, centerY, initialRotation, targetRef } =
    options;

  useEffect(() => {
    const handle = handleRef.current;
    if (!handle) return undefined;

    let dragging:
      | {
          startAngleDeg: number;
        }
      | null = null;

    const angleFromClient = (clientX: number, clientY: number): number => {
      // クライアント座標 → ステージ座標
      const view = useCanvasStore.getState().view;
      const stageX = (clientX - view.panX) / view.zoom;
      const stageY = (clientY - view.panY) / view.zoom;
      const dx = stageX - centerX;
      const dy = stageY - centerY;
      // atan2 は X 軸右方向を 0 として反時計回りに増えるので、UI の上方向を 0
      // とする 12 時起点（時計回り正）に変換する: angle = atan2(dy, dx) + 90°
      return Math.atan2(dy, dx) * (180 / Math.PI) + 90;
    };

    const onPointerDown = (event: PointerEvent): void => {
      if (event.button !== 0) return;
      dragging = { startAngleDeg: angleFromClient(event.clientX, event.clientY) };
      handle.setPointerCapture(event.pointerId);
      event.stopPropagation();
    };

    const computeRotation = (event: PointerEvent): number => {
      if (!dragging) return initialRotation;
      const currentAngle = angleFromClient(event.clientX, event.clientY);
      let next = initialRotation + (currentAngle - dragging.startAngleDeg);
      if (event.shiftKey) {
        next = Math.round(next / SNAP_ANGLE_DEG) * SNAP_ANGLE_DEG;
      }
      // -180..180 の範囲へ正規化
      next = ((next % 360) + 540) % 360 - 180;
      return next;
    };

    const onPointerMove = (event: PointerEvent): void => {
      if (!dragging) return;
      const next = computeRotation(event);
      const target = targetRef.current;
      if (target) {
        target.style.transform = `rotate(${next}deg)`;
      }
    };

    const onPointerUp = (event: PointerEvent): void => {
      if (!dragging) return;
      const next = computeRotation(event);
      dragging = null;
      if (handle.hasPointerCapture(event.pointerId)) {
        handle.releasePointerCapture(event.pointerId);
      }
      dispatcher.transformObject(objectId, { rotation: next });
    };

    handle.addEventListener("pointerdown", onPointerDown);
    handle.addEventListener("pointermove", onPointerMove);
    handle.addEventListener("pointerup", onPointerUp);
    handle.addEventListener("pointercancel", onPointerUp);
    return () => {
      handle.removeEventListener("pointerdown", onPointerDown);
      handle.removeEventListener("pointermove", onPointerMove);
      handle.removeEventListener("pointerup", onPointerUp);
      handle.removeEventListener("pointercancel", onPointerUp);
    };
  }, [handleRef, objectId, dispatcher, centerX, centerY, initialRotation, targetRef]);
}
